import React, { useEffect, useRef, useState } from 'react';
import { format, formatDistance, isAfter, parseISO, subDays } from 'date-fns';
import { ArrowRight, CheckCircle, MoreVertical, UserX } from 'react-feather';
import StatusToken from '../StatusTokens';
import { CommentActionEnum, DraftCommentStatusEnum, CommentFlag } from './CommentConstants';
import CommentActionMenu from './CommentActionMenu';
import Popup from '../Popup';
import FlexWrapper from '../../layouts/FlexWrapper';
import TextArea from '../Input/TextArea';
import Button from '../Buttons/Button';
import Divider from '../Divider';
import Tooltip from '../popups/Tooltip';
import "./CommentsSection.scss";

const Comment = ({ ticker, templateName, comment, idx, authors, userRole, handleCommentSubmit, refresh, setShowResolveComment, setText, workflowStatusId, requestId, uneditable }) => {
	const [showReplies, setShowReplies] = useState(false)
	const [showReplyInput, setShowReplyInput] = useState(false)
	const [replyInputValue, setReplyInputValue] = useState("")
	const [menuShowing, setMenuShowing] = useState(false)

	const replyInputRef = useRef();

	useEffect(() => {
		if (showReplyInput) {
			replyInputRef.current.focus()
		}
	}, [showReplyInput])


	const getAuthor = (email) => {
		if (!authors || !email) return "Unknown"
		const author = authors.find(author => author.email === email)
		return author ? author.name : "Unknown"
	}
	const getAuthorPhoto = (email) => {
		if (!authors || !email) return null
		const author = authors.find(author => author.email === email)
		return author ? author.photo : null
	}

	const hideShowReplies = () => {
		if (showReplies) {
			setShowReplies(false)
			setReplyInputValue("")
			setShowReplyInput(false)
		} else {
			setShowReplies(true)
		}
	}

	const showHideReplyInput = () => {
		if (showReplyInput) {
			setShowReplyInput(false)
			setReplyInputValue("")
		} else {
			setShowReplyInput(true)
		}
	}

	const parseDate = (date) => {
		if (!date) {
			return ""
		}
		try {
			const parsedDate = parseISO(date)
			return handleDateFormatting(parsedDate)
		} catch {
			return handleDateFormatting(date)
		}

	}

	const handleFeatureVisibilityOnSubmit = () => {
		setReplyInputValue("");
		setShowReplyInput(true);
		setShowReplies(true);
	}

	const handleDateFormatting = (date) => {
		const oneDayprevious = subDays(new Date(), 1)
		const dateIslessThanOneDayOld = isAfter(date, oneDayprevious)
		if (dateIslessThanOneDayOld) {
			return formatDistance(date, new Date())
		} else {
			return format(date, "dd MMM yyyy h:mm aaa")
		}
	}

	const handleCommentAction = async (comment, action) => {
		try {
			const response = await fetch(`/api/comments/${comment.commentId}?action=${action}`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ action })
			});

			if (response.ok) {
				refresh(); // Refresh the comments to show updated state
				setMenuShowing(false); // Close the menu
			} else {
				console.error('Failed to perform comment action:', action);
			}
		} catch (error) {
			console.error('Error performing comment action:', error);
		}
	};

	const handleResolveCommentAction = () => {
		const queryParams = new URLSearchParams({ requestId });
		if (workflowStatusId !== null && workflowStatusId !== undefined) {
			queryParams.append('workflowStatusId', workflowStatusId);
		}
		
		setShowResolveComment({
			header: 'Are you sure you want to resolve this comment?',
			message: 'This action cannot be undone. Please add a comment before submitting.',
			hasTextBox: true,
			handleClose: () => { setShowResolveComment(null); setText("") },
			cancelButton: { onClick: () => { setShowResolveComment(null); setText("") } },
			dto: { comment: "", flag: CommentFlag.RESOLVED_COMMENT, replyTo: comment.commentId, authorRole: userRole },
			path: `/comments/${btoa(ticker)}/${templateName}?${queryParams.toString()}`,
			callBack: () => refresh(),
			confirmButton: {
				label: "Yes, resolve",
				className: "button-variant-positive",
				disabled: true
			}
		})
	};


	return (
		<div
			key={comment.commentId + idx}
			className={`single-comment${showReplies ? " expanded" : ""} ${idx === 0 && !uneditable ? " first-comment" : ""} ${comment.isPersisted ? " persisted-comment" : ""}`}
		>
			<div className='comment-inner-wrapper' style={showReplies ? { borderBottom: '1px solid #E7EAF2' } : null}>
				<div className='comment-header'>
					<Tooltip
						innerStyle={{ padding: '0.5rem 0.75rem' }}
						label={
							<UserTooltipContent comment={comment} getAuthorPhoto={getAuthorPhoto} getAuthor={getAuthor} />
						}
					>
						{getAuthorPhoto(comment.author)
							? <img src={getAuthorPhoto(comment.author)} alt={getAuthor(comment.author)} className='comment-author-image' />
							: <div className="comment-author-image-initials">{getAuthor(comment.author).split(" ").map(val => val[0])}</div>
						}
					</Tooltip>
					<div className="comment-header-info">
						<div className="comment-header-info-top-row">
							<h4 className='comment-author'>{getAuthor(comment.author)}</h4>
							{comment.isPersisted && (
								<div className="persisted-indicator">
									<i className="colour-text-purple material-icons">lock</i>
								</div>
							)}
						</div>
						<div className="comment-header-info-bottom-row">
							<p className='comment-time'>{parseDate(comment.commentedOn)}</p>
						</div>
					</div>
					{!uneditable &&
						<Popup
							placement="left-start"
							onClick={() => setMenuShowing(!menuShowing)}
							triggerElement={<MoreVertical className="comment-action-menu-trigger" size={16} />}
							popupElement={<CommentActionMenu
								userRole={userRole}
								comment={comment}
								onClick={() => setMenuShowing(!menuShowing)}
								resolveOnClick={handleResolveCommentAction}
								onActionClick={(action) => handleCommentAction(comment, action)}
								handleClickAway={() => setMenuShowing(false)}
							/>}
						/>
					}
				</div>
				<p className='comment-message-body'>{comment.comment}</p>
				{comment.replies
					? <div className='comment-footer opacity-10' onClick={() => hideShowReplies()}>
						{comment.status === DraftCommentStatusEnum.RESOLVED ?
							<FlexWrapper align='center' gap='s'>
								<FlexWrapper align='center'>
									{comment.replies.length > 1 ? comment.replies.length + " Replies" : "1 Reply"}
									<i className='material-icons font-size-body-m' >{showReplies ? "expand_less" : "expand_more"}</i>
								</FlexWrapper>
								<Divider vertical height={16} />
								<FlexWrapper align='center' gap='xs'>
									{comment.flag === CommentFlag.REJECTED_COMMENT &&
										<div className='unresolved-comment-token'>
											Rejected
											<UserX />
										</div>
									}
									{comment.flag === CommentFlag.REJECTED_COMMENT && <ArrowRight size='0.75rem' style={{ opacity: 0.5 }} />}
									<Tooltip
										innerStyle={{ padding: '0.5rem 0.75rem' }}
										label={
											<FlexWrapper justify='center' direction='column'>
												<p className='font-weight-semi-bold'>{getAuthor(comment.author)}</p>
												<p>{format(parseISO(comment.updatedAt), "HH:mmaaa dd/MM/yyyy")}</p>
											</FlexWrapper>
										}
									>
										<div className='resolved-comment-token'>Resolved <CheckCircle /></div>
									</Tooltip>
								</FlexWrapper>
							</FlexWrapper> :
							<FlexWrapper align='center' gap='s'>
								<FlexWrapper align='center'>
									{comment.replies.length > 1 ? comment.replies.length + " Replies" : "1 Reply"}
									<i className='material-icons font-size-body-m' >{showReplies ? "expand_less" : "expand_more"}</i>
								</FlexWrapper>
								{(comment.flag === CommentFlag.RAISED_COMMENT || comment.flag === CommentFlag.REJECTED_COMMENT) &&
									<>
										<Divider vertical height={16} />
										<div className='unresolved-comment-token'>
											{comment.flag === CommentFlag.RAISED_COMMENT ? 'Unresolved' : 'Rejected'}
											{comment.flag === CommentFlag.REJECTED_COMMENT ? <UserX /> : ""}
										</div>
									</>
								}
							</FlexWrapper>}
					</div>
					: comment.status === DraftCommentStatusEnum.RESOLVED
						? <div className='comment-footer opacity-10'>
							<Tooltip
								innerStyle={{ padding: '0.5rem 0.75rem' }}
								label={
									<FlexWrapper justify='center' direction='column'>
										<p className='font-weight-semi-bold'>{getAuthor(comment.author)}</p>
										<p>{format(parseISO(comment.updatedAt), "HH:mmaaa dd/MM/yyyy")}</p>
									</FlexWrapper>
								}
							>
								<div className='resolved-comment-token'>Resolved <CheckCircle /></div>
							</Tooltip>
						</div>
						: <>
							<FlexWrapper gap='s' onClick={uneditable ? null :showHideReplyInput}>
								<div className='comment-footer'>{showReplyInput && comment.status !== DraftCommentStatusEnum.RAISED ? "" : uneditable ? "" : "Reply"}</div>
								{(comment.flag === CommentFlag.RAISED_COMMENT || comment.flag === CommentFlag.REJECTED_COMMENT) &&
									<>
										<Divider vertical height={16} />
										<div className='unresolved-comment-token'>
											{comment.flag === CommentFlag.RAISED_COMMENT ? 'Unresolved' : 'Rejected'}
											{comment.flag === CommentFlag.REJECTED_COMMENT ? <UserX /> : ""}
										</div>
									</>
								}
							</FlexWrapper>
							{showReplyInput &&
								<FlexWrapper direction="column" className="comments-input-container" align="start">
									<TextArea
										placeholder="Reply"
										className={`add-comment-text-area${comment.status === DraftCommentStatusEnum.RAISED ? " space-top" : ""}`}
										value={replyInputValue}
										onChange={(e) => setReplyInputValue(e.target.value)}
										maxlength={200}
										forwardRef={replyInputRef}
										resize='vertical'
										style={{ minHeight: '2rem', height: '2rem' }}
									/>
									<FlexWrapper gap='s' className='pb-s'>
										<Button
											style={{ fontSize: '0.85rem', padding: 0, minWidth: 50, height: 16, backgroundColor: 'transparent', border: 'none' }}
											size="small"
											variant='transparent'
											disabled={!userRole || !replyInputValue}
											fullWidth
											onClick={() => handleCommentSubmit(comment.commentId, replyInputValue, handleFeatureVisibilityOnSubmit)}
										>
											Submit
										</Button>
										<Button
											style={{ fontSize: '0.85rem', padding: 0, minWidth: 50, height: 16, backgroundColor: 'transparent', border: 'none' }}
											size="small"
											variant='transparent'
											fullWidth
											onClick={showHideReplyInput}
										>
											Cancel
										</Button>
									</FlexWrapper >
								</FlexWrapper >
							}
						</>
				}
			</div>
			{showReplies && comment?.replies?.sort((a, b) => a.commentedOn.localeCompare(b.commentedOn)).map((reply, replyIdx) => (
				<div className='single-reply' key={reply.id + replyIdx}>
					<div className={`reply-inner-wrapper ${(replyIdx + 1) < comment.replies.length ? "reply-border-bottom" : ""}`} >
						<div className='reply-header'>
							<Tooltip
								innerStyle={{ padding: '0.5rem 0.75rem' }}
								label={
									<UserTooltipContent comment={reply} getAuthorPhoto={getAuthorPhoto} getAuthor={getAuthor} />
								}
							>
								{getAuthorPhoto(reply.author)
									? <img src={getAuthorPhoto(reply.author)} alt={getAuthor(reply.author)} className='comment-author-image' />
									: <div className="comment-author-image-initials">{getAuthor(reply.author).split(" ").map(val => val[0])}</div>
								}
							</Tooltip>
							<div className="reply-header-info">
								<div className="reply-header-info-top-row">
									<h4 className='reply-author'>{getAuthor(reply.author)}</h4>
								</div>
								<div className="reply-header-info-bottom-row">
									<p className='reply-time'>{parseDate(reply.commentedOn)}</p>
								</div>
							</div>
						</div>
						{reply.flag === CommentFlag.RESOLVED_COMMENT && (replyIdx + 1) !== comment.replies.length &&
							<div className='comment-footer opacity-10' onClick={() => showHideReplyInput(reply.commentId)}>
								<Tooltip
									innerStyle={{ padding: '0.5rem 0.75rem' }}
									label={
										<FlexWrapper justify='center' direction='column'>
											<p className='font-weight-semi-bold'>{getAuthor(reply.author)}</p>
											<p>{format(parseISO(reply.updatedAt), "HH:mmaaa dd/MM/yyyy")}</p>
										</FlexWrapper>
									}
								>
									<div className='resolved-comment-token'>Resolved <CheckCircle /></div>
								</Tooltip>
							</div>
						}
						<p className='reply-message-body'>{reply.comment}</p>
						{(replyIdx + 1) === comment.replies.length &&
							<div className='comment-footer opacity-10' onClick={() => showHideReplyInput(comment.commentId)}>
								<FlexWrapper align='center' gap='s'>
									<div style={{ opacity: 0.5 }}>{reply.flag === CommentFlag.RESOLVED_COMMENT ? "Reply" : showReplyInput ? "" : uneditable ? "" :"Reply"}</div>
									{reply.flag === CommentFlag.RESOLVED_COMMENT &&
										<Tooltip
											innerStyle={{ padding: '0.5rem 0.75rem' }}
											label={
												<FlexWrapper justify='center' direction='column'>
													<p className='font-weight-semi-bold'>{getAuthor(reply.author)}</p>
													<p>{format(parseISO(reply.updatedAt), "HH:mmaaa dd/MM/yyyy")}</p>
												</FlexWrapper>
											}
										>
											<div className='resolved-comment-token'>Resolved <CheckCircle /></div>
										</Tooltip>
									}
								</FlexWrapper>
							</div>
						}
						{(replyIdx + 1) === comment.replies.length && showReplyInput &&
							<FlexWrapper direction="column" className="comments-input-container pr-m" align="start">
								<TextArea
									placeholder="Reply"
									className={`add-comment-text-area white${reply.flag === CommentFlag.RESOLVED_COMMENT ? " space-top" : ""}`}
									value={replyInputValue}
									onChange={(e) => setReplyInputValue(e.target.value)}
									maxlength={200}
									forwardRef={replyInputRef}
									resize='vertical'
									style={{ minHeight: '2rem', height: '2rem' }}
								/>
								<FlexWrapper justify={'space-between'} fullWidth >
									<FlexWrapper gap='s' className='pb-s'>
										<Button
											style={{ fontSize: '0.85rem', padding: 0, minWidth: 50, height: 16, backgroundColor: 'transparent', border: 'none' }}
											size="small"
											variant='transparent'
											disabled={!userRole || !replyInputValue}
											fullWidth
											onClick={() => handleCommentSubmit(comment.commentId, replyInputValue, handleFeatureVisibilityOnSubmit)}
										>
											Submit
										</Button>
										<Button
											style={{ fontSize: '0.85rem', padding: 0, minWidth: 50, height: 16, backgroundColor: 'transparent', border: 'none' }}
											size="small"
											variant='transparent'
											fullWidth
											onClick={showHideReplyInput}
										>
											Cancel
										</Button>
									</FlexWrapper>
									<p className='reply-time'>{replyInputValue ? replyInputValue.length : 0}/200</p>
								</FlexWrapper>
							</FlexWrapper >
						}
					</div>
				</div>
			))
			}
		</div >
	);
}

export default Comment;

const UserTooltipContent = ({ comment, getAuthorPhoto, getAuthor }) => {
	return <div style={{ padding: "0.25rem 0.5rem 0.25rem 0.25rem" }} onClick={(e) => { e.stopPropagation() }}>
		<FlexWrapper align='center' gap="m" justify='space-between'>

			<FlexWrapper gap='s'>
				{getAuthorPhoto(comment.author)
					? <img src={getAuthorPhoto(comment.author)} alt={getAuthor(comment.author)} className='comment-author-image small' />
					: <div className="comment-author-image-initials small">{getAuthor(comment.author).split(" ").map(val => val[0])}</div>
				}
				<div style={{ fontWeight: 600 }}>{getAuthor(comment.author)}</div>
				<StatusToken type="primary" label={comment.authorRole} />
			</FlexWrapper>
		</FlexWrapper>
		<FlexWrapper align='center' gap='s' style={{ marginTop: '0.25rem' }}>
			<FlexWrapper
				align='center'
				gap='xs'
				onClick={(e) => { e.stopPropagation() }}
			>
				<a className='user-tooltip-email' href={`mailto:${comment.author}`}>{comment.author}</a>
			</FlexWrapper>
		</FlexWrapper>
	</div>
};